import { PdfWatermarkMode, PdfWatermarkPosition, PdfWatermarkToken } from '@today/types'
import { FieldShell } from '@today/ui-components'
import type { ChangeEvent } from 'react'
import { ACCEPTED_IMAGE_TYPES, type PdfWatermarkDraft, type PdfWatermarkFieldErrors } from './pdf-watermark-draft'

// The field groups the PDF watermark form is built from (TO-495 REQ-011): each
// looks its error up by the API's field path, so a `details.field` refusal
// lands next to the field that caused it.

const TOKENS = Object.values(PdfWatermarkToken).map((token) => `{${token}}`)
const MODES: ReadonlyArray<[PdfWatermarkMode, string]> = [
	[PdfWatermarkMode.DISABLED, 'Disabled'],
	[PdfWatermarkMode.TEXT, 'Text'],
	[PdfWatermarkMode.IMAGE, 'Image'],
]
const POSITIONS: ReadonlyArray<[PdfWatermarkPosition, string]> = [
	[PdfWatermarkPosition.CENTER, 'Centre'],
	[PdfWatermarkPosition.TOP_LEFT, 'Top left'],
	[PdfWatermarkPosition.TOP_RIGHT, 'Top right'],
	[PdfWatermarkPosition.BOTTOM_LEFT, 'Bottom left'],
	[PdfWatermarkPosition.BOTTOM_RIGHT, 'Bottom right'],
]

const CONTROL_CLASS =
	'h-10 w-full rounded-ds-md border border-ds-border bg-ds-surface px-3 text-ds-base text-ds-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring disabled:cursor-not-allowed disabled:opacity-50'
const CHIP_CLASS =
	'rounded-ds-md border border-ds-border px-2 py-1 text-ds-sm text-ds-text disabled:cursor-not-allowed disabled:opacity-50'

/** An error that belongs to a group of controls rather than one input. */
export function ErrorLine({ message }: { message?: string | null }) {
	return message ? <p className="text-ds-sm text-ds-danger">{message}</p> : null
}

export interface DraftFieldsProps {
	draft: PdfWatermarkDraft
	readOnly: boolean
	errors: PdfWatermarkFieldErrors
	update: (patch: Partial<PdfWatermarkDraft>) => void
}

/** Disabled, text or image. */
export function ModeChoice({ draft, readOnly, errors, update }: DraftFieldsProps) {
	return (
		<fieldset className="flex flex-col gap-2">
			<legend className="text-ds-sm font-semibold text-ds-text">Watermark</legend>
			{MODES.map(([mode, label]) => (
				<label key={mode} className="flex items-center gap-2 text-ds-sm text-ds-text">
					<input
						type="radio"
						name="pdf-watermark-mode"
						value={mode}
						disabled={readOnly}
						checked={draft.mode === mode}
						onChange={() => update({ mode })}
					/>
					{label}
				</label>
			))}
			<ErrorLine message={errors.mode} />
		</fieldset>
	)
}

/** The template, its insertable tokens and the diagonal-stamp switch. */
export function TextFields({ draft, readOnly, errors, update }: DraftFieldsProps) {
	return (
		<div className="flex flex-col gap-3">
			<FieldShell
				label="Watermark text"
				description="Printed at the foot of every page, up to 200 characters."
				error={errors['text.template']}
			>
				<input
					type="text"
					className={CONTROL_CLASS}
					maxLength={200}
					disabled={readOnly}
					value={draft.template}
					onChange={(event: ChangeEvent<HTMLInputElement>) => update({ template: event.target.value })}
				/>
			</FieldShell>
			<fieldset className="flex flex-wrap gap-2" aria-label="Tokens">
				{TOKENS.map((token) => (
					<button
						key={token}
						type="button"
						className={CHIP_CLASS}
						aria-label={`Insert ${token}`}
						disabled={readOnly}
						onClick={() => update({ template: `${draft.template}${token}` })}
					>
						{token}
					</button>
				))}
			</fieldset>
			<label className="flex items-center gap-2 text-ds-sm text-ds-text">
				<input
					type="checkbox"
					disabled={readOnly}
					checked={draft.diagonalStamp}
					onChange={(event) => update({ diagonalStamp: event.target.checked })}
				/>
				Diagonal stamp
			</label>
			<ErrorLine message={errors['text.diagonalStamp']} />
		</div>
	)
}

export interface ImagePickerProps extends DraftFieldsProps {
	imageError: string | null
	onPick: (file: File | undefined) => void
}

/** The image file and its preview. */
export function ImagePicker({ draft, readOnly, errors, imageError, onPick }: ImagePickerProps) {
	return (
		<>
			<FieldShell
				label="Watermark image"
				description="PNG or JPEG, 256 KB or smaller."
				error={imageError ?? errors['image.contentBase64'] ?? errors['image.mimeType']}
			>
				<input
					type="file"
					accept={ACCEPTED_IMAGE_TYPES.join(',')}
					disabled={readOnly}
					onChange={(event: ChangeEvent<HTMLInputElement>) => onPick(event.target.files?.[0])}
				/>
			</FieldShell>
			{draft.image ? (
				<img
					className="max-h-24 max-w-48 rounded-ds-md border border-ds-border"
					alt="Watermark preview"
					src={`data:${draft.image.mimeType};base64,${draft.image.contentBase64}`}
				/>
			) : null}
		</>
	)
}

/** The image's opacity and position on the page. */
export function ImagePlacement({ draft, readOnly, errors, update }: DraftFieldsProps) {
	return (
		<>
			<FieldShell label="Opacity" description="From 0.05 to 1." error={errors['image.opacity']}>
				<input
					type="number"
					className={CONTROL_CLASS}
					min={0.05}
					max={1}
					step={0.05}
					disabled={readOnly}
					value={draft.opacity}
					onChange={(event: ChangeEvent<HTMLInputElement>) => update({ opacity: event.target.value })}
				/>
			</FieldShell>
			<FieldShell label="Position" error={errors['image.position']}>
				<select
					className={CONTROL_CLASS}
					disabled={readOnly}
					value={draft.position}
					onChange={(event: ChangeEvent<HTMLSelectElement>) =>
						update({ position: event.target.value as PdfWatermarkPosition })
					}
				>
					{POSITIONS.map(([value, label]) => (
						<option key={value} value={value}>
							{label}
						</option>
					))}
				</select>
			</FieldShell>
		</>
	)
}
